import { useSelector } from 'react-redux';

import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { PaymentGridSource } from '@makemydeal/dr-offer-redux';
import { FINANCE } from '@makemydeal/dr-platform-types';

import { BADGED_OFFER_TYPES } from './constants';

export enum DealTypeSource {
    PROGRAM = 'program',
    CALCULATED = 'calculated'
}

export const useDealTypeSource = (): DealTypeSource | undefined => {
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const currentCalcEndpoint = useSelector((state: StateTree) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, currentOfferType)
    );
    // Only `null`/`NaN` count as absent, so a genuine $0.00 payment is not read as unpriced.
    const isPaymentMissing = useSelector((state: StateTree) =>
        offerReduxSelectors.isMonthlyPaymentInvalid(state, currentOfferType)
    );

    // DE770996: an open grid column can be priced on a different engine than the deal, because only Lease adopts
    // the cell's engine at selection time. `undefined` when the redesign gate is off, on Flex Buy, or when the
    // column has no recorded engine - a closed or reopened grid included, since a close clears `columnSources`.
    // The deal endpoint is the fallback there and still names the last selected cell's engine: closing a Finance
    // grid on a user-priced cell moves the deal onto user-program-quotes (`handlePaymentGridToggleAction`).
    const selectedCellSource = useSelector((state: StateTree) =>
        offerReduxSelectors.getSelectedPaymentGridCellSource(state, currentOfferType)
    );

    const isCellSourcePending = useSelector((state: StateTree) =>
        offerReduxSelectors.isSelectedPaymentGridCellSourcePending(state, currentOfferType)
    );

    // `isFlexBuyPayment` always reads the FINANCE bucket, so it only means anything on a Finance deal.
    const isFlexBuyDeal = currentOfferType === FINANCE && isFlexBuyPayment;

    // The toggle is not re-read: `DealTypeDropdown` renders this only under `enableExplicitLenderP1`. An
    // `Unavailable` column renders blank, so it has no source to report.
    const canBadge =
        BADGED_OFFER_TYPES.includes(currentOfferType) &&
        !isFlexBuyDeal &&
        !isPaymentMissing &&
        !isCellSourcePending &&
        selectedCellSource !== PaymentGridSource.Unavailable;

    const isProgramSourced =
        selectedCellSource === undefined
            ? currentCalcEndpoint === CALC_ENDPOINTS.programQuotes
            : selectedCellSource === PaymentGridSource.ProgramQuotes;

    return !canBadge ? undefined : isProgramSourced ? DealTypeSource.PROGRAM : DealTypeSource.CALCULATED;
};
